import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "results", "placeholder", "selectButton", "clearButton", "hiddenInput", "dropdownInput"]
  static values = {
    url: String,
    minLength: { type: Number, default: 2 },
    invoiceNumber: String,
    secondCompany: { type: Boolean, default: false },
    autoSubmit: { type: Boolean, default: true },
    allowAll: { type: Boolean, default: false },
    allLabel: { type: String, default: "Visi" },
    allClearsSelection: { type: Boolean, default: false }
  }

  initialize() {
    this.submit = this.debounce(this.submit.bind(this), 300)
    this.initialCompaniesLoaded = false
  }

  connect() {
    this.boundHandleClickOutside = (event) => {
      if (!this.element.contains(event.target)) {
        this.hideResults()
      }
    }
    this.boundHandleKeydown = (event) => {
      if (event.key === "Escape" && this.hasDropdownTarget && !this.dropdownTarget.classList.contains("hidden")) {
        event.preventDefault()
        this.hideDropdown()
        this.expandedTrigger()?.focus()
      }
    }
    document.addEventListener('click', this.boundHandleClickOutside)
    document.addEventListener('keydown', this.boundHandleKeydown)
    this.setExpanded(false)

    const paramName = this.secondCompanyValue ? 'second_company_id' : 'company_id'
    const hasCompanySelected = new URLSearchParams(window.location.search).get(paramName)

    if (this.hasClearButtonTarget && hasCompanySelected) {
      this.clearButtonTarget.classList.remove('hidden')
    }
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleClickOutside)
    document.removeEventListener('keydown', this.boundHandleKeydown)
  }

  focus() {
    if (!this.hasInputTarget) return

    if (!this.inputTarget.value && !this.initialCompaniesLoaded) {
      this.initialCompaniesLoaded = true
      this.submit("")
    }
    this.showResults()
  }

  search(event) {
    const query = event.target.value.toLowerCase()
    this.submit(query)
    this.showResults()
  }

  submit(query) {
    const paramName = this.secondCompanyValue ? 'second_company_id' : 'company_id'
    const hiddenInput = this.element.querySelector(`input[name="${paramName}"]`) || 
                       this.element.querySelector('input[name="company_id"]')
    const companyId = hiddenInput ? hiddenInput.value : ''
    
    // If this is the second company filter, exclude the first company from results
    let excludeCompanyId = ''
    if (this.secondCompanyValue) {
      const firstCompanyInput = document.querySelector('input[name="company_id"]')
      excludeCompanyId = firstCompanyInput ? firstCompanyInput.value : ''
    }
    
    const url = `${this.urlValue}?query=${encodeURIComponent(query)}&company_id=${companyId}&exclude_company_id=${excludeCompanyId}`
    
    fetch(url)
      .then(response => response.json())
      .then(data => {
        this.resultsTarget.innerHTML = this.renderResults(data)
        this.showResults()
      })
      .catch(error => {
        console.error("Fetch failed:", error)
      })
  }

  renderResults(companies) {
    const hasResults = companies.length > 0

    if (!hasResults && !this.allowAllValue) {
      return `
        <div class="p-1 bg-white">
          <div class="flex items-center justify-center py-4 text-sm text-gray-500">
            Įmonių nerasta
          </div>
        </div>
      `
    }

    let resultsHTML = '<div class="p-1 bg-white">'

    // Add "All" option as first item if enabled
    if (this.allowAllValue) {
      resultsHTML += `
        <button type="button"
                class="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-gray-50 font-medium text-gray-700 border-b border-gray-100"
                data-action="click->company-autocomplete#selectCompany"
                data-company-id="all"
                data-company-name="${this.escapeHtml(this.allLabelValue)}">
          ${this.escapeHtml(this.allLabelValue)}
        </button>
      `
    }

    // Add company results
    if (hasResults) {
      resultsHTML += companies.map(company => `
        <button type="button"
                class="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-gray-50"
                data-action="click->company-autocomplete#selectCompany"
                data-company-id="${company.id}"
                data-company-name="${this.escapeHtml(company.name)}">
          ${this.escapeHtml(company.name)}
        </button>
      `).join('')
    } else if (this.allowAllValue) {
      // If allowAll is true but no companies found, still show message
      resultsHTML += `
        <div class="flex items-center justify-center py-4 text-sm text-gray-500">
          Įmonių nerasta
        </div>
      `
    }

    resultsHTML += '</div>'
    return resultsHTML
  }

  selectCompany(event) {
    const name = event.currentTarget.dataset.companyName
    const id = event.currentTarget.dataset.companyId
    const selectsAllCompanies = this.allClearsSelectionValue && !this.secondCompanyValue && id === 'all'

    this.placeholderTarget.textContent = name
    this.placeholderTarget.classList.remove('text-gray-500')
    this.placeholderTarget.classList.add('text-black', 'font-semibold')
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }
    if (this.hasDropdownInputTarget) {
      this.dropdownInputTarget.value = ""
    }

    const paramName = this.secondCompanyValue ? 'second_company_id' : 'company_id'
    const currentParams = new URLSearchParams(window.location.search)
    // Preserve ALL current filter parameters
    const form = this.element.closest('form')
    let hiddenInput = null

    // Prefer hiddenInputTarget if available (for custom named inputs like buyer_id/seller_id)
    if (this.hasHiddenInputTarget) {
      hiddenInput = this.hiddenInputTarget
    } else if (form) {
      hiddenInput = form.querySelector(`input[name="${paramName}"]`)
    }

    if (form) {
      const formData = new FormData(form)

      // Preserve all existing parameters from the form
      for (let [key, value] of formData.entries()) {
        if (key !== paramName && value) {
          currentParams.set(key, value)
        }
      }
    }

    if (selectsAllCompanies) {
      currentParams.delete('company_id')
      currentParams.delete('direction')
      currentParams.set('clear_company', '1')
    } else {
      currentParams.set(paramName, id)
      if (!this.secondCompanyValue) {
        currentParams.delete('clear_company')
        if (currentParams.get('second_company_id') === id) {
          currentParams.delete('second_company_id')
        }
      }
    }
    currentParams.delete('page')

    if (hiddenInput) {
      hiddenInput.value = selectsAllCompanies ? '' : id
    }

    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.remove('hidden')
    }

    // Company-centric UX: direction is derived per-row, no operation_type filter needed

    // Clean up empty parameters
    const paramsToDelete = []
    for (let [key, value] of currentParams.entries()) {
      if (value === '' || value === null || value === undefined) {
        paramsToDelete.push(key)
      }
    }
    paramsToDelete.forEach(key => currentParams.delete(key))

    this.hideResults()

    // Dispatch custom event for other controllers to listen to
    document.dispatchEvent(new CustomEvent('company-selected', {
      detail: { id: selectsAllCompanies ? null : id, name, element: this.element }
    }))

    // Only submit form if autoSubmit is enabled and form exists
    if (this.autoSubmitValue && form) {
      // Show loading overlay before navigation
      document.dispatchEvent(new CustomEvent("table-loading:show"))
      // Update URL before submitting to ensure clean params
      window.location.href = `${window.location.pathname}?${currentParams.toString()}`
    }
  }

  clearSelection(event) {
    event.preventDefault()

    const paramName = this.secondCompanyValue ? 'second_company_id' : 'company_id'
    const currentParams = new URLSearchParams(window.location.search)
    const form = this.element.closest('form')
    if (form) {
      for (const [key, value] of new FormData(form).entries()) {
        if (value) currentParams.set(key, value)
      }
    }

    currentParams.delete(paramName)
    currentParams.delete('page')
    if (!this.secondCompanyValue) {
      currentParams.delete('direction')
      currentParams.set('clear_company', '1')
    }

    this.resetUI()
    this.hideDropdown()

    this.updateUrlAndFetch(currentParams)

    document.dispatchEvent(new CustomEvent('company-cleared'))
  }

  resetUI() {
    const placeholder = this.secondCompanyValue ? "Kontrahentas" : "Įmonės paieška"
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.textContent = placeholder
      this.placeholderTarget.classList.remove('text-black', 'font-semibold')
      this.placeholderTarget.classList.add('text-gray-400')
    }
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }

    // Clear the form's hidden field
    const form = this.element.closest('form')
    const paramName = this.secondCompanyValue ? 'second_company_id' : 'company_id'
    const hiddenInput = form?.querySelector(`input[name="${paramName}"]`)
    if (hiddenInput) {
      hiddenInput.value = ''
    }

    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.add('hidden')
    }
  }

  updateUrlAndFetch(params) {
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`)
    this.fetchResults(params)
  }

  hideResults() {
    this.hideDropdown()
  }

  showResults() {
    this.showDropdown()
  }

  toggleDropdown(event) {
    if (this.hasDropdownTarget && this.dropdownTarget.classList.contains('hidden')) {
      this.resultsTarget.innerHTML = this.getLoadingHTML()
      if (this.hasInputTarget) {
        this.inputTarget.value = ""
      }
      if (this.hasDropdownInputTarget) {
        this.dropdownInputTarget.value = ""
      }
      this.submit("")
      this.showDropdown()
      // Focus dropdown input if available, otherwise main input
      if (this.hasDropdownInputTarget) {
        this.dropdownInputTarget.focus()
      } else if (this.hasInputTarget) {
        this.inputTarget.focus()
      }
    } else {
      this.hideDropdown()
    }
  }

  showDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove('hidden')
      this.setExpanded(true)
    }
  }

  hideDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add('hidden')
      this.setExpanded(false)
    }
  }

  setExpanded(expanded) {
    this.expandedTrigger()?.setAttribute('aria-expanded', expanded.toString())
  }

  expandedTrigger() {
    if (this.hasSelectButtonTarget) return this.selectButtonTarget
    if (this.hasPlaceholderTarget && this.placeholderTarget.matches('button')) return this.placeholderTarget
  }

  escapeHtml(unsafe) {
    if (typeof unsafe !== 'string') return unsafe
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  fetchResults(params) {
    // Show loading overlay before navigation
    document.dispatchEvent(new CustomEvent("table-loading:show"))
    window.location.href = `${window.location.pathname}?${params.toString()}`
  }

  getLoadingHTML() {
    return `
      <div class="p-1 bg-white">
        <div class="flex items-center justify-center py-4 text-sm text-gray-500">
          <div class="w-5 h-5 relative mr-3">
            <div class="w-5 h-5 rounded-full border-2 border-gray-300 border-t-gray-900 animate-spin"></div>
          </div>
          <span>Kraunama...</span>
        </div>
      </div>
    `
  }

  stopPropagation(event) {
    event.stopPropagation()
  }
}
